// HTTP + WebSocket client for the whitenoise server. The server only ever sees
// opaque ciphertext blobs (iv/ciphertext); all encryption lives in lib/crypto.
import type {
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
  RegistrationResponseJSON,
  AuthenticationResponseJSON,
} from "@simplewebauthn/browser";

export type CipherMode = "psk" | "pgp" | "ratchet";

export interface User {
  id: string;
  username: string;
}

export interface Room {
  id: string;
  label: string | null;
  kdf_salt: string;
  mode: CipherMode;
  expiry_seconds: number | null;
  created_by: string | null;
}

export interface PublicConfig {
  allowRegistration: boolean;
  requireInvite: boolean;
}

export interface WireMessage {
  id: string;
  seq: string;
  room_id: string;
  sender_id: string | null;
  iv: string;
  ciphertext: string;
  created_at: string;
}

// --- token storage -------------------------------------------------------
const TOKEN_KEY = "wn_token";

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string | null): void {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

// --- low-level request helper -------------------------------------------
interface RequestOpts {
  json?: unknown;
  bytes?: Uint8Array<ArrayBuffer>;
}

async function errorMessage(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as { error?: string };
    if (body && typeof body.error === "string") return body.error;
  } catch {
    /* non-JSON error body */
  }
  return res.statusText || `request failed (${res.status})`;
}

function authHeaders(extra?: Record<string, string>): Record<string, string> {
  const headers: Record<string, string> = { ...extra };
  const token = getToken();
  if (token) headers["authorization"] = `Bearer ${token}`;
  return headers;
}

async function request<T>(method: string, path: string, opts: RequestOpts = {}): Promise<T> {
  const headers = authHeaders();
  let body: BodyInit | undefined;
  if (opts.json !== undefined) {
    headers["content-type"] = "application/json";
    body = JSON.stringify(opts.json);
  } else if (opts.bytes !== undefined) {
    headers["content-type"] = "application/octet-stream";
    body = opts.bytes;
  }
  const res = await fetch(path, { method, headers, body });
  if (!res.ok) throw new Error(await errorMessage(res));
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

// --- typed API surface ---------------------------------------------------
export const api = {
  config(): Promise<PublicConfig> {
    return request<PublicConfig>("GET", "/api/config");
  },

  login(username: string, password: string): Promise<{ token: string; user: User }> {
    return request("POST", "/api/auth/login", { json: { username, password } });
  },

  register(
    username: string,
    password: string,
    invite?: string,
  ): Promise<{ token: string; user: User }> {
    return request("POST", "/api/auth/register", { json: { username, password, invite } });
  },

  me(): Promise<{ user: User; canTranscripts: boolean; isAdmin: boolean }> {
    return request("GET", "/api/me");
  },

  listRooms(): Promise<{ rooms: Room[] }> {
    return request("GET", "/api/rooms");
  },

  createRoom(label: string | undefined, mode: CipherMode): Promise<{ room: Room }> {
    return request("POST", "/api/rooms", { json: { label, mode } });
  },

  joinRoom(roomId: string): Promise<{ room: Room }> {
    return request("POST", `/api/rooms/${roomId}/join`);
  },

  setRoomExpiry(roomId: string, expirySeconds: number | null): Promise<{ room: Room }> {
    return request("POST", `/api/rooms/${roomId}/expiry`, { json: { expirySeconds } });
  },

  listMessages(roomId: string): Promise<{ messages: WireMessage[] }> {
    return request("GET", `/api/rooms/${roomId}/messages`);
  },

  postMessage(roomId: string, iv: string, ciphertext: string): Promise<{ message: WireMessage }> {
    return request("POST", `/api/rooms/${roomId}/messages`, { json: { iv, ciphertext } });
  },

  uploadFile(roomId: string, bytes: Uint8Array<ArrayBuffer>): Promise<{ fileId: string }> {
    return request("POST", `/api/rooms/${roomId}/files`, { bytes });
  },

  async downloadFile(fileId: string): Promise<Uint8Array<ArrayBuffer>> {
    const res = await fetch(`/api/files/${fileId}`, { headers: authHeaders() });
    if (!res.ok) throw new Error(await errorMessage(res));
    return new Uint8Array(await res.arrayBuffer());
  },

  // Encrypted transcript artifacts (returned as the raw {salt,iv,ct} text blob).
  // The index is built live from the source bucket; pass refresh to bypass the
  // server's short listing cache.
  async transcriptIndex(refresh = false): Promise<string> {
    const res = await fetch(`/api/transcripts/index${refresh ? "?refresh=1" : ""}`, {
      headers: authHeaders(),
    });
    if (!res.ok) throw new Error(await errorMessage(res));
    return res.text();
  },

  async transcriptItem(id: string): Promise<string> {
    const res = await fetch(`/api/transcripts/item/${id}`, { headers: authHeaders() });
    if (!res.ok) throw new Error(await errorMessage(res));
    return res.text();
  },

  // --- admin ---
  adminUsers(): Promise<{ users: AdminUser[]; admins: string[]; transcriptsUsers: string[] }> {
    return request("GET", "/api/admin/users");
  },
  adminSetGrants(
    id: string,
    grants: { transcripts?: boolean; admin?: boolean },
  ): Promise<{ user?: AdminUser }> {
    return request("POST", `/api/admin/users/${id}/grants`, { json: grants });
  },
  adminCreateUser(username: string, password: string): Promise<{ user: AdminUser }> {
    return request("POST", "/api/admin/users", { json: { username, password } });
  },
  adminSetPassword(id: string, password: string): Promise<{ ok: boolean }> {
    return request("POST", `/api/admin/users/${id}/password`, { json: { password } });
  },
  adminDeleteUser(id: string): Promise<{ ok: boolean }> {
    return request("DELETE", `/api/admin/users/${id}`);
  },
  adminRooms(): Promise<{ rooms: AdminRoom[] }> {
    return request("GET", "/api/admin/rooms");
  },
  adminDeleteRoom(id: string): Promise<{ ok: boolean }> {
    return request("DELETE", `/api/admin/rooms/${id}`);
  },
  adminInvites(): Promise<{ invites: AdminInvite[] }> {
    return request("GET", "/api/admin/invites");
  },
  adminMintInvites(count: number, note?: string): Promise<{ codes: string[] }> {
    return request("POST", "/api/admin/invites", { json: { count, note } });
  },
  adminRevokeInvite(code: string): Promise<{ ok: boolean }> {
    return request("POST", "/api/admin/invites/revoke", { json: { code } });
  },

  // --- WebAuthn (tap-to-login) ---
  webauthnRegisterOptions(): Promise<{ options: PublicKeyCredentialCreationOptionsJSON }> {
    return request("POST", "/api/webauthn/register/options");
  },
  webauthnRegisterVerify(
    response: RegistrationResponseJSON,
    label?: string,
  ): Promise<{ ok: boolean; credentialId: string }> {
    return request("POST", "/api/webauthn/register/verify", { json: { response, label } });
  },
  webauthnLoginOptions(
    username: string,
  ): Promise<{ options: PublicKeyCredentialRequestOptionsJSON; challengeId: string }> {
    return request("POST", "/api/webauthn/login/options", { json: { username } });
  },
  webauthnLoginVerify(
    challengeId: string,
    response: AuthenticationResponseJSON,
  ): Promise<{ token: string; user: User }> {
    return request("POST", "/api/webauthn/login/verify", { json: { challengeId, response } });
  },
};

export interface AdminUser {
  id: string;
  username: string;
  created_at: string;
  rooms: string;
  can_transcripts: boolean;
  is_admin: boolean;
}
export interface AdminRoom {
  id: string;
  label: string | null;
  mode: string;
  expiry_seconds: number | null;
  created_at: string;
  created_by: string | null;
  members: string;
  messages: string;
}
export interface AdminInvite {
  code: string;
  note: string | null;
  created_at: string;
  used_at: string | null;
  revoked_at: string | null;
  used_by: string | null;
}

// --- realtime channel ----------------------------------------------------
// Opens a WebSocket, subscribes to the room once connected, and invokes
// `onMessage` for each live message frame. Returns the socket so the caller
// can close() it on unmount.
export function openSocket(
  roomId: string,
  onMessage: (wire: WireMessage) => void | Promise<void>,
): WebSocket {
  const proto = location.protocol === "https:" ? "wss:" : "ws:";
  const token = getToken() ?? "";
  const ws = new WebSocket(`${proto}//${location.host}/ws?token=${encodeURIComponent(token)}`);

  ws.addEventListener("open", () => {
    ws.send(JSON.stringify({ type: "subscribe", roomId }));
  });

  ws.addEventListener("message", (ev) => {
    let frame: { type?: string; message?: WireMessage };
    try {
      frame = JSON.parse(typeof ev.data === "string" ? ev.data : "");
    } catch {
      return;
    }
    if (frame.type === "message" && frame.message) {
      void onMessage(frame.message);
    }
  });

  return ws;
}
